import dayjs from "dayjs";
import {
  Airline,
  Airport,
  BookedFlightItineraryWithDepartureTime,
  Flight,
  HomesReservation,
  ItineraryWithType,
  Reservation,
  TravelItineraryEnum,
  WatchAlertView,
} from "redmond";

const matchSearch = (
  normalizedParts: string[],
  fields: (string | undefined)[]
) => {
  const loweredFields = fields.map((f) => f?.toLowerCase() ?? "");
  return normalizedParts.every((part) =>
    loweredFields.some((field) => field.includes(part))
  );
};

const getLodgingSearchFields = (
  reservation: Reservation | HomesReservation
) => {
  const isHotel = "reservationId" in reservation;
  const guests = isHotel ? reservation.guests : [reservation.primaryGuest];
  const reservationBookingId = isHotel
    ? `H-${reservation.reservationId} ${reservation.reservationBookingId}`
    : `H-${reservation.id.value}`;

  const lodgingData = isHotel ? reservation.lodgingData : reservation.listing;
  const hotelName = lodgingData?.name ?? "";

  let hotelAddress = "";
  if (lodgingData?.address) {
    const addr = lodgingData.address;
    if (typeof addr === "string") {
      hotelAddress = addr;
    } else if ("line1" in addr) {
      hotelAddress = `${addr.line1} ${addr.line2} ${
        "city" in lodgingData ? lodgingData.city : ""
      } ${"country" in lodgingData ? lodgingData.country : ""}`;
    } else {
      hotelAddress = `${addr.city} ${
        "country" in lodgingData ? lodgingData.country : ""
      } ${addr.stateOrProvince}`;
    }
  } else if ("areaName" in lodgingData) {
    hotelAddress = `${lodgingData.address} ${lodgingData.state} ${lodgingData.countryCode}`;
  }

  const guestNames = guests.map((g) => `${g.givenName} ${g.surname}`).join(" ");

  return ["hotel", reservationBookingId, hotelName, hotelAddress, guestNames];
};

const getFlightSearchFields = (
  itinerary: Flight | BookedFlightItineraryWithDepartureTime,
  airports: Record<string, Airport | undefined>,
  airlines: Record<string, Airline | undefined>
) => {
  const { passengers, travelItinerary, id } = itinerary.bookedItinerary;
  const isMultiTicket =
    travelItinerary.TravelItinerary ===
    TravelItineraryEnum.MultiTravelItinerary;

  const allSegments = isMultiTicket
    ? travelItinerary.travelItineraries.flatMap((ti) =>
        ti.slices.flatMap((s) => s.segments)
      )
    : travelItinerary.slices.flatMap((s) => s.segments);

  const confirmationCodes = isMultiTicket
    ? travelItinerary.travelItineraries
        .map((ti) => `H-${ti.locators?.agent.unscopedValue || ""}`)
        .join(" ")
    : `H-${travelItinerary.locators?.agent.unscopedValue || ""}`;

  const flightNumber = allSegments
    .map(
      (seg) =>
        `${seg.marketingAirline.code}${seg.marketingAirline.flightNumber} ` +
        `${seg.operatingAirline?.code ?? ""}${
          seg.operatingAirline?.flightNumber ?? ""
        }`
    )
    .join(" ");

  const segmentsInfo = allSegments
    .map((s) => [
      s.cabinClassName,
      s.origin.locationCode,
      s.origin.terminalName,
      airports[s.origin.locationCode]?.name,
      airports[s.origin.locationCode]?.cityName,
      airports[s.origin.locationCode]?.servedCityName,
      s.destination.locationCode,
      airports[s.destination.locationCode]?.name,
      airports[s.destination.locationCode]?.cityName,
      airports[s.destination.locationCode]?.servedCityName,
      s.destination.terminalName,
      s.marketingAirline.flightNumber,
      s.marketingAirline.code,
      airlines[s.marketingAirline.code]?.displayName,
    ])
    .flat()
    .filter(Boolean)
    .join(" ");

  const allPassengers = [
    ...passengers.alone.map((p) => `${p.person.givenName} ${p.person.surname}`),
    ...passengers.withLapInfants.map(
      (p) => `${p.adult.person.givenName} ${p.adult.person.surname}`
    ),
  ];

  return [
    "flight",
    id,
    ...allPassengers,
    segmentsInfo,
    confirmationCodes,
    flightNumber,
  ];
};
const searchFlightItinerary = (
  itinerary: Flight | BookedFlightItineraryWithDepartureTime,
  airports: Record<string, Airport | undefined>,
  airlines: Record<string, Airline | undefined>
) => getFlightSearchFields(itinerary, airports, airlines);

export const matchSearchQuery = (
  search: string,
  airports: Record<string, Airport | undefined>,
  airlines: Record<string, Airline | undefined>
) => {
  const normalizedParts = search.trim().toLowerCase().split(" ");
  if (!normalizedParts.length) {
    return () => true;
  }

  return (
    itinerary:
      | ItineraryWithType
      | BookedFlightItineraryWithDepartureTime
      | WatchAlertView
  ) => {

    if ("type" in itinerary && search.length > 0) {
      const displayedType = itinerary.type === "Home" ? "vacation rental" : itinerary.type.toLowerCase()
      const queryMatchesType = displayedType.includes(search)
      // If search query matches displayed reservation type return result otherwise continue matching
      if (queryMatchesType) {
        return true
      }
    }

    // extract flight relevant data for search
    if ("bookedItinerary" in itinerary) {
      return matchSearch(
        normalizedParts,
        searchFlightItinerary(itinerary, airports, airlines)
      );
    }

    // extract hotel relevant data for search
    if ("reservation" in itinerary) {
      const { reservation } = itinerary;
      if ("experienceSelection" in reservation) {
        const { experienceDetails, bookingDetails, customerReservationId } =
          reservation;
        return matchSearch(normalizedParts, [
          "activity",
          "experience",
          experienceDetails?.name,
          experienceDetails?.destination,
          `H-${customerReservationId}`,
          bookingDetails?.bookerInfo
            ? `${bookingDetails.bookerInfo.firstName} ${bookingDetails.bookerInfo.lastName}`
            : undefined,
        ]);
      }

      return matchSearch(normalizedParts, getLodgingSearchFields(reservation));
    }

    // extract cars relevant data for search
    if ("bookResult" in itinerary) {
      const { driver } = itinerary.bookResult;

      const { vehicle } = itinerary.bookResult;

      const pickUp = `${itinerary.bookResult.pickUp.location.name} ${itinerary.bookResult.pickUp.location.address}`;

      const dropOff = `${itinerary.bookResult.dropOff.location.name} ${itinerary.bookResult.dropOff.location.address}`;

      const carName = `${vehicle.name}`;
      const driverName = `${driver.givenName} ${driver.surname}`;

      const bookingId = `H-${itinerary.bookResult.groundBookingId} H-${itinerary.bookResult.bookingId} H-${itinerary.bookResult.tripSummary?.userBookingId}`;

      return matchSearch(normalizedParts, [
        "car",
        carName,
        driverName,
        pickUp,
        dropOff,
        bookingId,
      ]);
    }

    // extract watch relevant data for search
    if ("key" in itinerary) {
      const { value } = itinerary.key;
      const { departureDate, returnDate, origin, destination } = value;
      const departureDateFormatted = dayjs(departureDate).format("MMM DD");
      const returnDateFormatted = dayjs(returnDate).format("MMM DD");
      const originName = airports[origin.code]?.name;
      const originCityName = airports[origin.code]?.cityName;
      const originServedCityName = airports[origin.code]?.servedCityName;
      const destinationName = airports[destination.code]?.name;
      const destinationCityName = airports[destination.code]?.cityName;
      const destinationServedCityName =
        airports[destination.code]?.servedCityName;
      const recommendation =
        itinerary.recommendation === "Buy" ? "buy book now" : "wait";

      return matchSearch(normalizedParts, [
        "watch",
        recommendation,
        departureDateFormatted,
        returnDateFormatted,
        origin.code,
        originName,
        originCityName,
        originServedCityName,
        destination.code,
        destinationName,
        destinationCityName,
        destinationServedCityName,
      ]);
    }

    // extract package relevant data for search
    if ("flight" in itinerary && "hotel" in itinerary) {
      return matchSearch(normalizedParts, [
        `H-${itinerary.customerConfirmationId}`,
        ...getFlightSearchFields(
          itinerary.flight.itinerary,
          airports,
          airlines
        ),
        ...getLodgingSearchFields(itinerary.hotel.itinerary.reservation),
        "packages",
      ]);
    }

    return true;
  };
};
